/** @jsxImportSource @emotion/react */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { toast } from 'utils/toast'
import { PageLoader } from 'components/PageLoader'
import TabsNavigation from 'components/TabsNavigation'
import { prepareEditorHtml } from 'components/Editor'
import { QUESTION_MEDIA_PATH_S3 } from 'constants/images'
import { TabsItem } from 'types/tabsItem'
import {
  FIND_ONE_EXTENSION_EXAM_SECTION,
  UPDATE_EXTENSION_EXAM_QUESTION,
  UPDATE_EXTENSION_EXAM_PASSAGE,
} from 'queries/extensionExams'
import { updateExtensionQuestion, updateExtensionQuestionVariables } from '__generatedTypes__/updateExtensionQuestion'
import { updateExtensionPassage, updateExtensionPassageVariables } from '__generatedTypes__/updateExtensionPassage'
import {
  findOneExtensionExamSection,
  findOneExtensionExamSectionVariables,
  findOneExtensionExamSection_findOneExtensionExamSection_extensionPassage as Passage,
} from '__generatedTypes__/findOneExtensionExamSection'
import * as upgradePageClasses from 'pagesNew/UpgradePage/styles'
import { useHistory } from 'hooks/useHistory'
import * as classes from './styles'
import { QuestionPreview } from './QuestionPreview'
import { PassagePreview } from './PassagePreview'
import { QuestionEditModal, QuestionWithIndex } from './QuestionEditModal'
import { PassageEditModal } from './PassageEditModal'

const NO_PASSAGE_ID = 'no-passage-id'

const prepareForSubmit = async (question: QuestionWithIndex): Promise<QuestionWithIndex> => {
  const [mainSolution, answerSolution] = await Promise.all([
    prepareEditorHtml(question.mainSolution ?? '', QUESTION_MEDIA_PATH_S3),
    Promise.all(
      (question.answerSolution ?? []).map(
        async (solution) => await prepareEditorHtml(solution ?? '', QUESTION_MEDIA_PATH_S3)
      )
    ),
  ])

  return {
    ...question,
    mainSolution,
    answerSolution,
  }
}

export const ExamSection = () => {
  const apolloClient = useApolloClient()
  const { sectionId = '' } = useParams()
  const { navigate } = useLink()
  const { loadingHistory, populateHistoryWithUser } = useHistory()
  const [activeTab, setActiveTab] = useState<TabsItem | null>(null)
  const [isPassageExpanded, setIsPassageExpanded] = useState<boolean>(false)
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null)
  const [isQuestionEditModalOpen, setIsQuestionEditModalOpen] = useState<boolean>(false)
  const [selectedQuestion, setSelectedQuestion] = useState<QuestionWithIndex | null>(null)
  const [isPassageEditModalOpen, setIsPassageEditModalOpen] = useState<boolean>(false)
  const [updateExtensionQuestion] = useMutation<updateExtensionQuestion, updateExtensionQuestionVariables>(
    UPDATE_EXTENSION_EXAM_QUESTION
  )
  const [updateExtensionPassage] = useMutation<updateExtensionPassage, updateExtensionPassageVariables>(
    UPDATE_EXTENSION_EXAM_PASSAGE
  )
  const { data, loading } = useQuery<findOneExtensionExamSection, findOneExtensionExamSectionVariables>(
    FIND_ONE_EXTENSION_EXAM_SECTION,
    {
      variables: {
        id: sectionId,
      },
      fetchPolicy: 'network-only',
    }
  )
  const examSection = data?.findOneExtensionExamSection ?? null

  const handleUpdateQuestion = useCallback(
    async (questionData: QuestionWithIndex) => {
      try {
        const question = await prepareForSubmit(questionData)
        const history = populateHistoryWithUser(question.history)
        await updateExtensionQuestion({
          variables: {
            params: {
              id: question.id,
              mainSolution: question.mainSolution ?? '',
              mainVideoSolution: question.mainVideoSolution ?? '',
              answerSolution: question.answerSolution ?? [],
              history,
            },
          },
        })
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({
            __typename: 'ExtensionQuestionType',
            id: question.id,
          }),
          fields: {
            mainSolution: () => question.mainSolution ?? '',
            mainVideoSolution: () => question.mainVideoSolution ?? '',
            answerSolution: () => question.answerSolution ?? [],
            history: () => history,
          },
        })
        toast.success('Question updated successfully')
        setIsQuestionEditModalOpen(false)
      } catch (e) {
        toast.error('Question update failed')
      }
    },
    [updateExtensionQuestion, apolloClient, populateHistoryWithUser]
  )

  const handleUpdatePassage = useCallback(
    async (passageData: Passage) => {
      try {
        await updateExtensionPassage({
          variables: {
            params: {
              id: passageData.id,
              videoWalkthrough: passageData.videoWalkthrough ?? '',
            },
          },
        })
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({
            __typename: 'ExtensionPassageType',
            id: passageData.id,
          }),
          fields: {
            videoWalkthrough: () => passageData.videoWalkthrough ?? '',
          },
        })
        toast.success('Passage updated successfully')
        setIsPassageEditModalOpen(false)
      } catch (e) {
        toast.error('Passage update failed')
      }
    },
    [updateExtensionPassage, apolloClient]
  )

  const passageById = useMemo(() => {
    if (!examSection?.extensionPassage?.length) {
      return {}
    }

    return examSection.extensionPassage.reduce(
      (acc, passage) => ({
        ...acc,
        [passage.id]: passage,
      }),
      {} as Record<string, Passage>
    )
  }, [examSection?.extensionPassage])

  const noPassageQuestions = examSection?.extensionQuestion ?? []

  const minQuestionOrder = useMemo(() => {
    const questionOrders = [
      examSection?.extensionQuestion?.[0]?.order ?? null,
      ...(examSection?.extensionPassage?.map(({ extensionQuestion }) => extensionQuestion?.[0]?.order ?? null) ?? []),
    ].filter((order) => order != null)

    return Math.min(...(questionOrders as number[])) ?? 0
  }, [examSection])

  const tabs: TabsItem[] = useMemo(() => {
    if (!examSection) {
      return []
    }

    const noPassageTabs = examSection.extensionQuestion?.length
      ? [
          {
            id: NO_PASSAGE_ID,
            title: 'No Passage',
            error: examSection.extensionQuestion?.some((question) => !question.mainSolution),
          },
        ]
      : []

    const passageTabs =
      examSection.extensionPassage?.map((passage, index) => ({
        id: passage.id,
        title: `Passage #${index + 1}`,
        error: passage.extensionQuestion?.some((question) => !question.mainSolution),
      })) ?? []

    return [...noPassageTabs, ...passageTabs]
  }, [examSection])

  useEffect(() => {
    setActiveTab((prevActiveTab) =>
      prevActiveTab && tabs.find(({ id }) => id === prevActiveTab.id) ? prevActiveTab : tabs[0] ?? null
    )
  }, [tabs])

  useEffect(() => {
    if (!loading && !examSection) {
      navigate(cmsRoutes.CARS_PLUGIN)
      toast.error('Failed to load data, redirecting to Plugin Dashboard')
    }
  }, [loading, examSection])

  if (loading || !activeTab || !examSection || loadingHistory) {
    return <PageLoader />
  }

  const selectedPassage = passageById[activeTab.id]
  const questions = selectedPassage?.extensionQuestion ?? noPassageQuestions

  return (
    <section css={upgradePageClasses.container}>
      <Typography css={upgradePageClasses.header} variant="title3">
        <span css={classes.headSubtitle}>{examSection.extensionExam.title}</span>
        {examSection.title}
      </Typography>

      <TabsNavigation css={classes.tabs} tabs={tabs} value={activeTab} onChange={setActiveTab} />

      {selectedPassage && (
        <>
          <Typography variant="body1" css={classes.sectionTitle}>
            Passage
          </Typography>
          <PassagePreview
            title={activeTab.title}
            passageContent={selectedPassage.content}
            videoWalkthrough={selectedPassage.videoWalkthrough}
            expanded={isPassageExpanded}
            onExpandClick={() => setIsPassageExpanded((prev) => !prev)}
            onEditClick={() => {
              setIsPassageEditModalOpen(true)
            }}
          />
        </>
      )}

      <Typography variant="body1" css={classes.sectionTitle}>
        AAMC Solution Builder
      </Typography>

      {questions.length ? (
        <>
          {questions.map((question) => (
            <QuestionPreview
              key={question.id}
              questionIndex={question.order - minQuestionOrder + 1}
              question={question}
              expanded={question.id === expandedQuestionId}
              onExpandClick={() => setExpandedQuestionId((prev) => (prev === question.id ? null : question.id))}
              onEditClick={() => {
                setSelectedQuestion({ ...question, questionIndex: question.order - minQuestionOrder + 1 })
                setIsQuestionEditModalOpen(true)
              }}
            />
          ))}
        </>
      ) : (
        <div>No questions</div>
      )}
      <QuestionEditModal
        isOpen={isQuestionEditModalOpen}
        question={selectedQuestion}
        onSubmit={handleUpdateQuestion}
        onClose={() => setIsQuestionEditModalOpen(false)}
      />
      <PassageEditModal
        isOpen={isPassageEditModalOpen}
        passage={selectedPassage ?? null}
        passageTitle={selectedPassage ? activeTab.title : ''}
        onSubmit={handleUpdatePassage}
        onClose={() => setIsPassageEditModalOpen(false)}
      />
    </section>
  )
}
